import React, { useContext, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import DContext from '../../context/DContext';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { flightsService } from '../../services/flights.service';
import { airlinesService } from '../../services/airlines.service';
import { airportsService } from '../../services/airports.service';
import { countriesService } from '../../services/countries.service';
import { citiesService } from '../../services/cities.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';

// list APIs return either a plain array or { data: [] }
const toArray = (rd) => (Array.isArray(rd) ? rd : rd?.data || []);

const AddFlight = () => {
  const location = useLocation();
  const editFlightId = location.state?.flightId;
  const isEdit = !!editFlightId;

  const { authState, authDispatch, logout } = useContext(DContext);
  const { user } = authState;

  const [loading, setLoading] = useState(false);
  const [airlinesDropdown, setAirlinesDropdown] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [depCityOptions, setDepCityOptions] = useState([]);
  const [arrCityOptions, setArrCityOptions] = useState([]);
  const initialFlightDetail = {
    airlineId: "",
    flightName: "",
    flightCode: "",
    flightNumber: "",
    depAirportId: "",
    depAirportCode: "",
    depCityName: "",
    depCountry: "",
    depCountryId: "",
    arrAirportId: "",
    arrAirportCode: "",
    arrCityName: "",
    arrCountry: "",
    arrCountryId: "",
    depTime: "",
    arrTime: "",
    depTerminal: "",
    arrTerminal: ""
  };
  const [flightDetail, setFlightDetail] = useState(initialFlightDetail);

  useEffect(() => {
    loadAirlines();
    loadCountries();
    if (isEdit) loadFlightDetail();
  }, [editFlightId]);

  // In edit mode the flight only carries country/city names: resolve the country id once countries are loaded
  useEffect(() => {
    if (!isEdit || countryOptions.length === 0) return;
    ["dep", "arr"].forEach((side) => {
      const name = flightDetail[side + "Country"];
      if (!name || flightDetail[side + "CountryId"]) return;
      const match = countryOptions.find(c => c.label.toLowerCase() === name.toLowerCase());
      if (match) {
        setFlightDetail((f) => ({ ...f, [side + "CountryId"]: match.value }));
        loadCities(side, match.value);
      }
    });
  }, [countryOptions, flightDetail.depCountry, flightDetail.arrCountry]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadFlightDetail = async () => {
    try {
      setLoading(true);
      const res = await flightsService.details(editFlightId);
      if (res?.success) {
        const f = res.resData?.flight ?? res.resData;
        setFlightDetail({
          airlineId: f?.airline?.id || "",
          flightName: f?.flight_name || "",
          flightCode: f?.flight_code || "",
          flightNumber: f?.flight_number || "",
          depAirportId: f?.departureAirport?.id || "",
          depAirportCode: f?.departureAirport?.airport_code || "",
          depCityName: f?.departureAirport?.city_name || "",
          depCountry: f?.departureAirport?.country_name || "",
          depCountryId: "",
          arrAirportId: f?.arrivalAirport?.id || "",
          arrAirportCode: f?.arrivalAirport?.airport_code || "",
          arrCityName: f?.arrivalAirport?.city_name || "",
          arrCountry: f?.arrivalAirport?.country_name || "",
          arrCountryId: "",
          depTime: (f?.departure_time || "").slice(0, 5),
          arrTime: (f?.arrival_time || "").slice(0, 5),
          depTerminal: f?.departure_terminal || "",
          arrTerminal: f?.arrival_terminal || ""
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  // Airline dropdown: airlines > list (status = active)
  const loadAirlines = async () => {
    try {
      const res = await airlinesService.list({ page: 1, status: "active" });
      if (res?.success) {
        setAirlinesDropdown(toArray(res.resData).map(a => ({
          value: a.id, label: a.airline_name, airline_name: a.airline_name, airline_code: a.airline_code
        })));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  // Departure / Arrival airport dropdowns: airports > list (status = active), searched on the server
  const loadAirports = async (search = "") => {
    try {
      const res = await airportsService.list({ page: 1, limit: 50, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return toArray(res.resData).map(a => ({
          value: a.id,
          label: `${a.airport_name} (${a.airport_code}), ${a.city_name}`,
          airport_code: a.airport_code,
          city_name: a.city_name,
          country_name: a.country_name
        }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  // Country dropdown: countries > list
  const loadCountries = async () => {
    try {
      const res = await countriesService.list({ page: 1, status: "active" });
      if (res?.success) {
        setCountryOptions((res.resData?.data || []).map(c => ({ value: c.id, label: c.name })));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  // City dropdown: cities > list based on the selected country
  const loadCities = async (side, countryId) => {
    const setter = side === "dep" ? setDepCityOptions : setArrCityOptions;
    if (!countryId) { setter([]); return; }
    try {
      const res = await citiesService.list({ page: 1, status: "active", country_id: Number(countryId) });
      if (res?.success) {
        setter((res.resData?.data || []).map(c => ({ value: c.name, label: c.name })));
      } else {
        setter([]);
        toast.error(res?.message);
      }
    } catch (err) {
      setter([]);
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  const handleSelectChange = (selectedOption) => {
    setFlightDetail({
      ...flightDetail,
      flightName: selectedOption?.airline_name || "",
      airlineId: selectedOption?.value || "",
      flightCode: selectedOption?.airline_code || ""
    })
  };

  // side = "dep" | "arr": copies the airport and syncs the country / city dropdowns from it
  const handleAirportChange = (side, selectedOption) => {
    const countryName = selectedOption?.country_name || "";
    const match = countryOptions.find(c => c.label.toLowerCase() === countryName.toLowerCase());
    setFlightDetail({
      ...flightDetail,
      [side + "AirportId"]: selectedOption?.value || "",
      [side + "AirportCode"]: selectedOption?.airport_code || "",
      [side + "CityName"]: selectedOption?.city_name || "",
      [side + "Country"]: countryName,
      [side + "CountryId"]: match?.value || ""
    });
    loadCities(side, match?.value);
  };

  const handleCountryChange = (side, selectedOption) => {
    setFlightDetail({
      ...flightDetail,
      [side + "CountryId"]: selectedOption?.value || "",
      [side + "Country"]: selectedOption?.label || "",
      [side + "CityName"]: ""
    });
    loadCities(side, selectedOption?.value);
  };

  const handleCityChange = (side, selectedOption) => {
    setFlightDetail({ ...flightDetail, [side + "CityName"]: selectedOption?.value || "" });
  };

  const handleChange = (e) => {
    setFlightDetail({ ...flightDetail, [e.target.name]: e.target.value });
  }

  const handleTimeChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // remove non-digits

    if (value.length > 2) {
      value = value.slice(0, 2) + ":" + value.slice(2, 4); // insert ":"
    }

    if (value.length > 5) {
      value = value.slice(0, 5); // max length 5 (HH:MM)
    }

    setFlightDetail({ ...flightDetail, [e.target.name]: value });
  };

  const validate = () => {
    if (!flightDetail.airlineId) return "Please select an airline";
    if (!String(flightDetail.flightNumber).trim()) return "Flight number is required";
    if (!flightDetail.depAirportId) return "Please select a departure airport";
    if (!flightDetail.arrAirportId) return "Please select an arrival airport";
    if (flightDetail.depAirportId === flightDetail.arrAirportId) return "Departure and arrival airport cannot be the same";
    if (!/^\d{2}:\d{2}$/.test(flightDetail.depTime)) return "Enter departure time as HH:MM";
    if (!/^\d{2}:\d{2}$/.test(flightDetail.arrTime)) return "Enter arrival time as HH:MM";
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const err = validate();
      if (err) { toast.error(err); return; }
      setLoading(true);

      const payload = {
        airline_code: flightDetail.flightCode,
        departure_airport_code: flightDetail.depAirportCode,
        arrival_airport_code: flightDetail.arrAirportCode,
        flight_code: flightDetail.flightCode,
        flight_name: flightDetail.flightName,
        flight_number: String(flightDetail.flightNumber),
        departure_time: flightDetail.depTime,
        arrival_time: flightDetail.arrTime,
        departure_terminal: flightDetail.depTerminal,
        arrival_terminal: flightDetail.arrTerminal,
      };
      const res = isEdit
        ? await flightsService.update(editFlightId, payload)
        : await flightsService.create({ ...payload, status: "active" });
      if (res?.success) {
        showSwal("Success", res.message, "success");
        if (!isEdit) {
          setFlightDetail(initialFlightDetail);
          setDepCityOptions([]);
          setArrCityOptions([]);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const GRAD = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
  const labelStyle = { fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#6c757d", marginBottom: 6 };
  const inputStyle = { borderRadius: 8, border: "1px solid #dfe3ec", fontSize: 13, height: 40, background: "#fff" };
  const readOnlyStyle = { ...inputStyle, background: "#eef0f8", color: "#4a5568" };
  const selectStyles = {
    control: (base, state) => ({
      ...base, borderRadius: 8, border: state.isFocused ? "1px solid #667eea" : "1px solid #dfe3ec", minHeight: 40,
      fontSize: 13, boxShadow: state.isFocused ? "0 0 0 3px rgba(102,126,234,.15)" : "none", "&:hover": { borderColor: "#667eea" }
    }),
    option: (base, state) => ({
      ...base, fontSize: 13,
      background: state.isSelected ? "#667eea" : state.isFocused ? "#eef0ff" : "#fff",
      color: state.isSelected ? "#fff" : "#1f2a44",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  };
  const sectionCard = { background: "#fff", borderRadius: 12, border: "1px solid #e8ebf7", padding: 20, marginBottom: 16 };
  const sectionTitle = (icon, text) => (
    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ fontSize: 14, color: "#4b3f8f" }}>
      <i className={"bi " + icon} style={{ color: "#667eea" }}></i> {text}
    </h6>
  );
  const field = (label, required, node) => (
    <div className="col-12 col-md-6">
      <div style={labelStyle}>{label}{required && <span className="text-danger"> *</span>}</div>
      {node}
    </div>
  );
  const title = isEdit ? "Update Flight" : "Add Flight";

  return (
    <>
      <section className="section dashboard">
        {/* Page header */}
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "#1a202c", fontSize: 20 }}>{isEdit ? "Update Flight" : "Add New Flight"}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <Link to="/" style={{ color: "#8a9ab5" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/flight/list" style={{ color: "#8a9ab5" }}>Flights</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>{title}</span>
            </nav>
          </div>
          <Link to="/flight/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Flights List
          </Link>
        </div>

        <form onSubmit={handleSubmit}>

            {/* Airline Detail */}
            <div style={sectionCard}>
              {sectionTitle("bi-airplane", "Airline Detail")}
              <div className="row g-3">
                {field("Airline", true,
                  <Select
                    options={airlinesDropdown}
                    value={flightDetail.airlineId ? { value: flightDetail.airlineId, label: flightDetail.flightName } : null}
                    onChange={handleSelectChange}
                    placeholder="Select airline"
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Flight Code", false,
                  <div className="d-flex align-items-center gap-2">
                    <input type="text" className="form-control" placeholder="Auto-filled from airline" name="flightCode"
                      value={flightDetail?.flightCode || ""} readOnly style={{ ...readOnlyStyle, textTransform: "uppercase" }} />
                    {flightDetail?.flightCode && (
                      <img src={`https://images.kiwi.com/airlines/32/${flightDetail?.flightCode}.png`}
                        style={{ width: 28, height: 28, objectFit: "contain" }} alt="airline logo" />
                    )}
                  </div>
                )}
                {field("Flight Number", true,
                  <input type="number" min="0" className="form-control" placeholder="e.g. 202" name="flightNumber"
                    value={flightDetail?.flightNumber || ""} onChange={handleChange} style={inputStyle} />
                )}
              </div>
            </div>

            {/* Departure */}
            <div style={sectionCard}>
              {sectionTitle("bi-airplane-engines", "Departure Detail")}
              <div className="row g-3">
                {field("Departure Airport", true,
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    name="dep_airport_dropdown"
                    value={flightDetail.depAirportId ? { value: flightDetail.depAirportId, label: `${flightDetail.depAirportCode}, ${flightDetail.depCityName}` } : null}
                    placeholder="Search departure airport"
                    loadOptions={loadAirports}
                    onChange={(o) => handleAirportChange("dep", o)}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Airport Code", false,
                  <input type="text" className="form-control" placeholder="IATA code" name="depAirportCode"
                    value={flightDetail?.depAirportCode || ""} readOnly style={{ ...readOnlyStyle, textTransform: "uppercase" }} />
                )}
                {field("Country", false,
                  <Select
                    options={countryOptions}
                    value={flightDetail.depCountryId ? { value: flightDetail.depCountryId, label: flightDetail.depCountry } : null}
                    onChange={(o) => handleCountryChange("dep", o)}
                    placeholder="Select country"
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("City", false,
                  <Select
                    options={depCityOptions}
                    isDisabled={!flightDetail.depCountryId}
                    value={flightDetail.depCityName ? { value: flightDetail.depCityName, label: flightDetail.depCityName } : null}
                    onChange={(o) => handleCityChange("dep", o)}
                    placeholder={flightDetail.depCountryId ? "Select city" : "Select country first"}
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            {/* Arrival */}
            <div style={sectionCard}>
              {sectionTitle("bi-airplane-fill", "Arrival Detail")}
              <div className="row g-3">
                {field("Arrival Airport", true,
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    name="arr_airport_dropdown"
                    value={flightDetail.arrAirportId ? { value: flightDetail.arrAirportId, label: `${flightDetail.arrAirportCode}, ${flightDetail.arrCityName}` } : null}
                    placeholder="Search arrival airport"
                    loadOptions={loadAirports}
                    onChange={(o) => handleAirportChange("arr", o)}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Airport Code", false,
                  <input type="text" className="form-control" placeholder="IATA code" name="arrAirportCode"
                    value={flightDetail?.arrAirportCode || ""} readOnly style={{ ...readOnlyStyle, textTransform: "uppercase" }} />
                )}
                {field("Country", false,
                  <Select
                    options={countryOptions}
                    value={flightDetail.arrCountryId ? { value: flightDetail.arrCountryId, label: flightDetail.arrCountry } : null}
                    onChange={(o) => handleCountryChange("arr", o)}
                    placeholder="Select country"
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("City", false,
                  <Select
                    options={arrCityOptions}
                    isDisabled={!flightDetail.arrCountryId}
                    value={flightDetail.arrCityName ? { value: flightDetail.arrCityName, label: flightDetail.arrCityName } : null}
                    onChange={(o) => handleCityChange("arr", o)}
                    placeholder={flightDetail.arrCountryId ? "Select city" : "Select country first"}
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            {/* Schedule & Terminals */}
            <div style={sectionCard}>
              {sectionTitle("bi-clock", "Schedule & Terminals")}
              <div className="row g-3">
                {field("Departure Time", true,
                  <input type="text" className="form-control" placeholder="HH:MM" name="depTime" maxLength={5}
                    value={flightDetail?.depTime || ""} onChange={handleTimeChange} style={inputStyle} />
                )}
                {field("Arrival Time", true,
                  <input type="text" className="form-control" placeholder="HH:MM" name="arrTime" maxLength={5}
                    value={flightDetail?.arrTime || ""} onChange={handleTimeChange} style={inputStyle} />
                )}
                {field("Departure Terminal", false,
                  <input type="text" className="form-control" placeholder="e.g. 1" name="depTerminal"
                    value={flightDetail?.depTerminal || ""} onChange={handleChange} style={inputStyle} />
                )}
                {field("Arrival Terminal", false,
                  <input type="text" className="form-control" placeholder="e.g. 2" name="arrTerminal"
                    value={flightDetail?.arrTerminal || ""} onChange={handleChange} style={inputStyle} />
                )}
              </div>
            </div>

            {/* Footer buttons */}
            <div className="d-flex justify-content-end gap-2">
              <Link to="/flight/list" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #dfe3ec", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                {isEdit ? "Update Flight" : "Add Flight"}
              </button>
            </div>

        </form>
      </section>
    </>
  );
}

export default AddFlight;
